import type { BreakdownSnapshot, BreakdownDocumentContents, JobBreakdown, Rates } from '@jobos/shared';
import { fromFlatItems, type FlatItem } from './types';
import { buildDocContents, type DocLineItemInput } from './buildDocContents';
import type { LineItemData } from './LineItemList';

// crypto.randomUUID() is already used elsewhere in the breakdown editor
// (see createSubItem in subItemTypes.ts) — no uuid dependency needed.
const uuid = () => crypto.randomUUID();

// Snapshot is id-less; JobBreakdown / the editor widgets need stable ids.
// Assign fresh UUIDs on load. Output shape feeds the existing toFlatItems(),
// which only reads id/category/description/totalCost/subItems/templateId/
// itemDate/sortOrder — so the `as JobBreakdown` cast is safe even though we
// omit job-only fields (jobId/createdAt/updatedAt). Do NOT add those fields.
export function snapshotToJobBreakdown(snapshot: BreakdownSnapshot): JobBreakdown {
  const mapSub = (s: BreakdownSnapshot['lineItems'][number]['subItems'][number]) => ({
    id: uuid(), type: s.type, hours: s.hours ?? null, rate: s.rate ?? null,
    rateType: s.rateType ?? null, unitKey: s.unitKey ?? null, amount: s.amount ?? null, note: s.note ?? null,
    markupRate: s.markupRate ?? null, sortOrder: s.sortOrder,
    // Required by BreakdownSubItem (LaborSubItem | DirectSubItem) — omitted
    // from snapshots (template context has no lineItemId/timestamps).
    lineItemId: '' as const,
    createdAt: '' as const,
    updatedAt: '' as const,
  });
  const mapLine = (li: BreakdownSnapshot['lineItems'][number]) => ({
    id: uuid(), category: li.category, description: li.description ?? null,
    sortOrder: li.sortOrder, templateId: li.templateId ?? null, itemDate: li.itemDate ?? null,
    totalCost: li.totalCost, subItems: li.subItems.map(mapSub),
    // Required by BreakdownLineItem — omitted from snapshots.
    jobId: '' as const,
    projectId: null,
    createdAt: '' as const,
    updatedAt: '' as const,
  });
  return {
    taxRate: Number(snapshot.taxRate ?? 0),
    markupRate: Number(snapshot.markupRate ?? 0),
    roundingPrecision: snapshot.roundingPrecision == null ? null : Number(snapshot.roundingPrecision),
    totalCost: snapshot.totalCost,
    lineItems: (snapshot.lineItems ?? []).map(mapLine),
    projects: (snapshot.projects ?? []).map((p) => ({
      id: uuid(), name: p.name, sortOrder: p.sortOrder, lineItems: p.lineItems.map(mapLine),
      // Required by BreakdownProject — omitted from snapshots.
      jobId: '' as const,
      createdAt: '' as const,
      updatedAt: '' as const,
    })),
  } as JobBreakdown;
}

// Read rounding off the loaded snapshot (separate from Rates — see below).
export function roundingFromSnapshot(snapshot: BreakdownSnapshot): number | null {
  return snapshot.roundingPrecision == null ? null : Number(snapshot.roundingPrecision);
}

// Editor state (FlatItem[]) + rates + rounding → the id-bearing contents
// payload. This MIRRORS `buildDocContentsPayload` in useBreakdownSave.ts
// (~line 163) and delegates to the shared `buildDocContents` serializer,
// which assigns positional sub-item sortOrder and normalizes nulls. Do NOT
// re-implement the mapping: `SubItemForm` has no `sortOrder` field, and its
// hours/rate/amount live behind a discriminated union accessed via `'x' in s`.
//
// NOTE: `roundingPrecision` is a SEPARATE param — the shared `Rates` type is
// `{ taxRate, markupRate }` only; do not put rounding inside a Rates object.
export function flatItemsToContents(
  flatItems: FlatItem[],
  rates: Rates,
  rounding: number | null,
): BreakdownDocumentContents {
  const { looseItems, projects } = fromFlatItems(flatItems);
  const toDocLineItem = (item: LineItemData, projectId: string | null): DocLineItemInput => ({
    id: item.id, category: item.category, description: item.description ?? null,
    sortOrder: item.sortOrder ?? 0, templateId: item.templateId ?? null, itemDate: item.itemDate ?? null,
    projectId,
    subItems: item.subItems.map((sub) => ({
      id: sub.id, type: sub.type,
      hours: 'hours' in sub ? (sub as any).hours ?? null : null,
      rate: 'rate' in sub ? (sub as any).rate ?? null : null,
      rateType: 'rateType' in sub ? (sub as any).rateType ?? null : null,
      unitKey: 'unitKey' in sub ? (sub as any).unitKey ?? undefined : undefined,
      amount: 'amount' in sub ? (sub as any).amount ?? null : null,
      note: sub.note ?? null,
      markupRate: (sub as any).markupRate ?? null,
    })),
  });
  return buildDocContents({
    taxRate: rates.taxRate, markupRate: rates.markupRate, roundingPrecision: rounding,
    projects: projects.map((p) => ({ id: p.id, name: p.name, sortOrder: p.sortOrder ?? 0 })),
    lineItems: [
      ...looseItems.map((li) => toDocLineItem(li, null)),
      ...projects.flatMap((p) => p.lineItems.map((li) => toDocLineItem(li, p.id))),
    ],
  });
}
